import { log } from "console";
import EngineUtils from "../EngineUtils";
import { EQuantityTypesLow } from "../interfaces";
import { logDebug, logError, logWarning } from "../logger";
import { EExtraDataMappingType, ILineItemForUI } from "./EstimateEngineTypes";
import SharedUtils, { noop } from "../SharedUtils";
import { IXactimateToolRowData } from "../Azure";

export const availableMeasurements = [
	"Floor(F)",
	"Ceiling(C)",
	"Walls(W)",
	"Wall and ceiling(WC)",
	"Perimeter of ceiling(PC)",
	"Perimeter of floor(PF)",
	"Long wall(LL)",
	"Short wall(SL)",
	"Volume of room(V)",
	"Floor below stairs(FBS)",
	"Walls below stairs(WBS)",
	"SF",
	"LF",
	"RL",
	"EA",
	"SQ",
	"P",
	"R",
	"Hip",
	"Val",
	"Hr",
	"DA",
	"GL",
	"WK",
	"CF",
	"MO",
];

export const shortHandMeasurements = ["wc", "f", "c", "w", "pc", "pf", "pr", "ll", "sl", "v", "fbs", "wbs", "sq", "hip", "val", "p", "r"];

export const units = ["sf", "lf", "rl", "ea", "sq", "hr", "da", "gl", "wk", "cf", "mo"];

export const allUnits = [...units, ...shortHandMeasurements];

/**
 * Extracts unit from a formula string and returns the calculation without the unit
 * @param formula - Formula string like "PF+5SF" or "W*2LF"
 * @returns Object with calc (formula without unit) and unit (extracted unit)
 * @example
 * extractUnitFromFormula("PF+5SF") => { calc: "PF+5", unit: "sf" }
 * extractUnitFromFormula("W*2LF") => { calc: "W*2", unit: "lf" }
 * extractUnitFromFormula("10") => { calc: "10", unit: "" }
 */
export function extractUnitFromFormula(formula: string): { calc: string; unit: string } {
	if (!formula || typeof formula !== "string") {
		return { calc: formula || "", unit: "" };
	}

	// Create a regex pattern that matches any unit at the end of the formula
	// Make it case-insensitive and match units that might be at the end
	const unitsPattern = units.join("|");
	const regex = new RegExp(`(${unitsPattern})$`, "i");

	const match = formula.match(regex);

	if (match) {
		const unit = match[1].toLowerCase();
		const calc = formula.slice(0, -match[1].length);
		return { calc, unit };
	}

	return { calc: formula, unit: "" };
}

const availableMeasurementsLow = availableMeasurements.map((m) => m.toLowerCase());

export enum EMeasurementCellNames {
	Walls = "Walls (sq ft)",
	Floors = "Floors (sq ft)",
	Ceilings = "Ceilings (sq ft)",
	FloorPerimeter = "Floor (perimeter-ft.)",
	CeilingPerimeter = "Ceiling (perimeter-ft.)",
}
export const measurementCellNames = [
	[EMeasurementCellNames.Walls, "ft.²", "sf"],
	[EMeasurementCellNames.Floors, "ft.²", "sf"],
	[EMeasurementCellNames.Ceilings, "ft.²", "sf"],
	[EMeasurementCellNames.FloorPerimeter, "ft", "ft"],
	[EMeasurementCellNames.CeilingPerimeter, "ft", "ft"],
];

export interface IQuantityFormula {
	initialValue: number;
	base: string;
	operator: string;
	modifierValue: number;
	modifierSuffix: string;
	extra?: string;
	// is true if the formula's quantity contains multiple different quantities and units, e.g. "1W+2SQFT"; if this is true,
	// then the quantities and formulas are stored in rawFormula
	isMultiUnit?: boolean;
	rawFormula?: string;
}

export default class EngineSharedUtils {
	public static readonly prefixOrder: string[] = [
		"contents",
		"electrical",
		"plumbing",
		"walls_and_ceiling",
		"shelving",
		"doors_and_windows",
		"paint_and_stain",
		"flooring",
		"finish_trim",
		"final_cleaning",
		// type 1.1
		"bathroom_kitchen",
		"appliances",
		"general_hvac",
		"staircase",
		"kitchen_cabinetry",
		"plumbing",
		"laundry_room",
	];

	static readonly formulaRegex =
		/^(?:(?<initial>(?:\d+(?:\.\d+)?|\d+\/\d+))(?:(?:\s*(?:[*x])\s*)|\s+))?(?<base>pc|pf|sf|lf|rl|w|c)(?:\s*(?<operator>[+-])\s*(?<modifier>\d+(?:\.\d+)?)(?<modifierSuffix>sf|lf|rl)?)?$/;

	static getFormulaString(formula: IQuantityFormula): string {
		// logDebug("formula", formula);
		if (formula.base.includes("flr_rpl")) {
			return "flr_rpl";
		}
		// Guard against throwing an error when something went wrong in formula creation
		if (formula.initialValue == null) {
			return "na";
		}
		let base = formula.base;
		let showInitialValue = true;
		if (shortHandMeasurements.includes(formula.base)) {
			base = formula.base.toUpperCase();
			showInitialValue = formula.initialValue !== 1;
		}
		if (formula.operator) {
			return `${showInitialValue ? parseFloat(formula.initialValue.toFixed(2)) : ""}${base} ${formula.operator} ${parseFloat(
				formula.modifierValue.toFixed(2),
			)}${formula.modifierSuffix}`;
		} else {
			return `${showInitialValue ? parseFloat(formula.initialValue.toFixed(2)) : ""}${base}`;
		}
	}

	static readonly allowedPairs = [
		["sf", "sf"],
		["lf", "lf"],
		["rl", "rl"],
		["ea", "ea"],
		["sq", "sq"],
		["hr", "hr"],
		["da", "da"],
		["gl", "gl"],
		["wk", "wk"],
		["cf", "cf"],
		["mo", "mo"],
		["w", "sf"],
		["wc", "sf"],
		["flr_rpl", "sf"],
		["c", "sf"],
		["f", "sf"],
		["pc", "lf"],
		["pf", "lf"],
		["pf", "sf"],
		["p", "lf"],
		["r", "lf"],
		["hip", "lf"],
		["val", "lf"],
	];

	static getMeasurementTypeBase(base: string): string {
		const pair = EngineSharedUtils.allowedPairs.find((p) => p[0] === base);
		if (pair) {
			return pair[1];
		}
		return "";
	}

	static getAllowedUnitsFromXactimateMapping(
		lineItem: Partial<ILineItemForUI>,
		xactimateMapping: { [key: string]: IXactimateToolRowData },
	): string[] {
		if (!lineItem.lineItem || !lineItem.lineItemId) {
			return [];
		}
		const mappings = Object.values(xactimateMapping).filter(
			(m) => m.selectedLineItemId === lineItem.lineItemId && m.acceptedLineString?.toLowerCase() === lineItem.lineItem!.toLowerCase(),
		);
		if (mappings.length > 0) {
			const uniqueUnits = Array.from(
				mappings.reduce((uniqueSet, mapping) => {
					if (mapping && typeof mapping.quantity === "string" && mapping.quantity.trim() !== "") {
						uniqueSet.add(mapping.quantity.toLowerCase());
					}
					return uniqueSet;
				}, new Set<string>()),
			);
			return uniqueUnits;
		}
		return [];
	}

	static checkAndFixFormula(formula: IQuantityFormula, defualtMeasurement: string, allowedUnits?: string[]): string {
		// logDebug("formulaTest", formula);
		defualtMeasurement = defualtMeasurement.toLowerCase();
		formula.base = formula.base.toLowerCase();
		formula.modifierSuffix = formula.modifierSuffix?.toLowerCase();

		if (formula.initialValue === 0) return "Initial value cannot be zero";
		if (formula.extra && formula.extra.trim().length > 0) return `Extra unparsed content "${formula.extra}" in formula`;
		if (!formula.base) {
			formula.base = defualtMeasurement;
			if (allowedUnits?.length) {
				formula.base = allowedUnits[0];
			}
		}
		if (formula.base && formula.modifierSuffix) {
			// check if in allowed pairs
			const pair = EngineSharedUtils.allowedPairs.find((p) => p[0] === formula.base && p[1] === formula.modifierSuffix);
			if (!pair) return `Invalid formula "${formula.base} and ${formula.modifierSuffix}"`;
		}
		if (!formula.modifierSuffix && formula.operator !== "/" && formula.operator !== "*" && formula.operator !== "x") {
			if (!EngineSharedUtils.allowedPairs.find((p) => p[0] === formula.base)?.length) {
				return `Invalid formula`;
			}
			formula.modifierSuffix = EngineSharedUtils.allowedPairs.find((p) => p[0] === formula.base)![1];
		}
		if (formula.operator) {
			if (!formula.modifierValue) {
				return `+ or - found but no modifier number in "${formula}"`;
			}
		}
		if (allowedUnits?.length) {
			const validQuantities = allowedUnits.filter((q) => q === formula.modifierSuffix);
			if (!validQuantities.length) {
				return `Invalid units, please use ${allowedUnits.map((q) => `"${q.toUpperCase()}"`).join(", ")}`;
			}
		}

		// logDebug("formulaTestEnd", formula);
		return "";
	}

	static extractFormulaFromString(formulaString: string): IQuantityFormula | null {
		// logDebug("~~~~~formula", formulaString);
		formulaString = formulaString.toLowerCase().trim();
		let remaining = formulaString;
		let initial: string | undefined,
			base: string | undefined,
			operator: string | undefined,
			modifier: string | undefined,
			initialValue: number | undefined,
			modifierValue: number | undefined,
			modifierSuffix: string | undefined;
		initialValue = 1;
		modifierValue = 0;
		operator = "";
		modifierSuffix = "";

		if (formulaString.includes("flr_rpl")) {
			return { initialValue: 1, base: "flr_rpl", operator: "", modifierValue: 0, modifierSuffix: "" };
		}

		// Extract initial number (int, float, or fraction)
		let m = remaining.match(/^((?:\d+\/\d+)|(?:\d+(?:\.\d+)?))\s*/);
		if (m) {
			initial = m[1];
			remaining = remaining.slice(m[0].length);
			if (initial.includes("/")) {
				const [num, den] = initial.split("/");
				const numerator = parseFloat(num);
				const denominator = parseFloat(den);
				if (denominator !== 0) initialValue = numerator / denominator;
				else {
					logError(`Division by zero in fraction "${initial}"`);
					return null;
				}
			} else {
				initialValue = parseFloat(initial);
			}
			// Remove multiplier if present (* or x with optional spaces)
			m = remaining.match(/^(?:[*x])\s*/i);
			if (m) remaining = remaining.slice(m[0].length);
		}
		// logDebug("initial", initialValue);
		// logDebug("remaining", remaining);

		// Extract base unit (pc, pf, sf, lf, rl, w, or c)
		m = remaining.match(/^(pc|pf|cf|wk|hr|wc|w|c|f|sf|lf|rl|ea|sq|da|gl|mo|hip|val|p|r)\s*/i);
		if (m) {
			base = m[1];
			remaining = remaining.slice(m[0].length);
		} else {
			// this is just a number situation
			return { initialValue, base: "", operator: "", modifierValue: 0, modifierSuffix: "" };
		}

		// logDebug("base", base);
		// logDebug("remaining", remaining);
		// Extract optional operator (+ or -) and modifier number (int or float)
		m = remaining.match(/^([+-/*x])\s*/);
		if (m) {
			operator = m[1];
			remaining = remaining.slice(m[0].length);
			logDebug("operator", operator);
			// logDebug("remaining", remaining);
			m = remaining.match(/^(\d+(?:\.\d+)?)/);

			if (m) {
				modifier = m[1];
				remaining = remaining.slice(m[0].length);
				modifierValue = parseFloat(modifier);
				logDebug("modifier", modifierValue);
				// logDebug("remaining", remaining);
			} else {
				logError(`Operator found but no modifier number in "${formulaString}"`);
				return null;
			}
			// logDebug("testing", remaining, "for sf");
			// Optional modifier suffix (sf, lf, or rl)
			m = remaining.match(/^\s*(sf|lf|rl|ea)\s*/i);
			if (m) {
				modifierSuffix = m[1];
				remaining = remaining.slice(m[0].length);
				// logDebug("modifierSuffix", modifierSuffix);
				// logDebug("remaining", remaining);
			}
		}
		// logDebug("last remaining", remaining);
		if (remaining.trim().length > 0) {
			logWarning(`Extra unparsed content "${remaining}" in formula "${formulaString}"`);
		}
		// logDebug("returning", { initialValue, base, operator, modifierValue, modifierSuffix, extra: remaining });
		return { initialValue, base, operator, modifierValue, modifierSuffix, extra: remaining };
	}

	static getQuantityStringForLineItem(lineItem: ILineItemForUI, errorQuantity = "na"): string {
		// lineItem.lineItem.includes("hung") && logDebug("lineItem", lineItem);
		if (lineItem.formula) {
			return this.getFormulaString(lineItem.formula);
		} else if (lineItem.qnm?.length) {
			if (lineItem.qnm[0].measurement === "flr_rpl") {
				return "flr_rpl";
			}
			return `${lineItem.qnm[0].quantity} ${lineItem.qnm[0].measurement}`;
		}
		return errorQuantity;
	}
	private static _getFinalQuantityForFormula(
		lineItem: ILineItemForUI,
		pageId: string,
		quantityBeforeModifierFunc: (id: string, measurementType: string) => number,
	): string {
		// if (lineItem.lineItem === "walls seal with pva primer 1 coat" && lineItem.formula?.base === "wc") {
		// 	noop();
		// }
		// we support on these here
		const measurementToCellnameMap: { [key: string]: string } = {
			f: EMeasurementCellNames.Floors,
			c: EMeasurementCellNames.Ceilings,
			w: EMeasurementCellNames.Walls,
			// wc: EMeasurementCellNames.Walls, // TODO ::
			pc: EMeasurementCellNames.CeilingPerimeter,
			pf: EMeasurementCellNames.FloorPerimeter,
		};
		const formula = lineItem.formula!;
		if (formula.base === "flr_rpl") {
			const floorMeasurments = quantityBeforeModifierFunc(pageId, measurementToCellnameMap["f"]);
			return `${Math.ceil(1.15 * floorMeasurments)} sf`;
		}
		if (!formula.initialValue || formula.initialValue <= 0) {
			return "na";
		}
		let quantityBeforeModifier = 1; //quantityBeforeModifierFunc(pageId, measurementToCellnameMap[formula.base]);
		if (measurementToCellnameMap[formula.base] !== undefined) {
			// must be f, c, w, pc, pf
			quantityBeforeModifier = quantityBeforeModifierFunc(pageId, measurementToCellnameMap[formula.base]); //return EngineSharedUtils.getFormulaString(formula);
		}
		const suffix = formula.modifierSuffix || formula.base;

		if (!formula.operator) {
			const ret = `${parseFloat((formula.initialValue * quantityBeforeModifier).toFixed(2))} ${suffix}`;
			return ret;
		} else {
			if (formula.operator === "+") {
				return `${parseFloat((formula.initialValue * quantityBeforeModifier + formula.modifierValue).toFixed(2))} ${suffix}`;
			} else if (formula.operator === "-") {
				return `${parseFloat((formula.initialValue * quantityBeforeModifier - formula.modifierValue).toFixed(2))} ${suffix}`;
			} else if (formula.operator === "*" || formula.operator === "x") {
				return `${parseFloat((formula.initialValue * quantityBeforeModifier * formula.modifierValue).toFixed(2))} ${suffix}`;
			} else if (formula.operator === "/") {
				return `${parseFloat(((formula.initialValue * quantityBeforeModifier) / formula.modifierValue).toFixed(2))} ${suffix}`;
			}
		}
		return "na";
	}
	static getFinalQuantity(
		lineItem: ILineItemForUI,
		pageId: string,
		quantityBeforeModifierFunc: (id: string, measurementType: string) => number,
	): string {
		if (lineItem.formula) {
			return this._getFinalQuantityForFormula(lineItem, pageId, quantityBeforeModifierFunc);
		}
		// this is here for backward compatibility
		// with time we shouldn't have anymore line items without formula
		const measurementToCellnameMap: { [key: string]: string } = {
			"walls(w)": EMeasurementCellNames.Walls,
			"ceiling(c)": EMeasurementCellNames.Ceilings,
			"floor(f)": EMeasurementCellNames.Floors,
			"perimeter of floor(pf)": EMeasurementCellNames.FloorPerimeter,
			"perimeter of ceiling(pc)": EMeasurementCellNames.CeilingPerimeter,
			f: EMeasurementCellNames.Floors,
			c: EMeasurementCellNames.Ceilings,
			w: EMeasurementCellNames.Walls,
			// wc: EMeasurementCellNames.Walls, // TODO ::
			pc: EMeasurementCellNames.CeilingPerimeter,
			pf: EMeasurementCellNames.FloorPerimeter,
			floor: EMeasurementCellNames.Floors,
			ceiling: EMeasurementCellNames.Ceilings,
			walls: EMeasurementCellNames.Walls,
			wall: EMeasurementCellNames.Walls,
			floors: EMeasurementCellNames.Floors,
		};
		// if (tuple.tupleId === 'electrical_baseboard_electric_heater') {
		// 	noop();
		// }
		const measurement = lineItem.qnm.length ? lineItem.qnm[0].measurement : "";
		if (measurement && measurement.includes("flr_rpl")) {
			const floorMeasurments = quantityBeforeModifierFunc(pageId, measurementToCellnameMap["f"]);
			return `${Math.ceil(1.15 * floorMeasurments)} sf`;
		}
		// logDebug('measurement', measurement);
		const quantity = (lineItem.qnm.length && lineItem.qnm[0].quantity) || 1;
		if (!measurement) {
			return "na";
		}
		function getMeasurement(measurement: string) {
			const cell = measurementCellNames.find((m) => m[0] === measurementToCellnameMap[measurement]);
			if (!cell) {
				return measurement;
			}
			return cell[2];
		}
		if (EngineUtils.availableMeasurementsLow.includes(measurement) || Object.keys(measurementToCellnameMap).includes(measurement)) {
			const quantityBeforeModifier = quantityBeforeModifierFunc(pageId, measurementToCellnameMap[measurement]);
			const onlyModifiers = lineItem.mappingExtraData.filter(
				(m) =>
					m.type === EExtraDataMappingType.QuantityModifier &&
					m.mappings![0].mapping?.quantityType !== EQuantityTypesLow.QuantityAsValue &&
					m.mappings![0].mapping?.quantityType !== EQuantityTypesLow.NoQuantity,
			);
			let quantityType: string | undefined;
			let quantityValue: number = 1;

			// TODO :: we need to take care of the substract case
			if (onlyModifiers.length === 0 && Object.keys(measurementToCellnameMap).includes(measurement)) {
				if (quantity < 0) {
					quantityType = EQuantityTypesLow.QauntityAsSubtract;
					quantityValue = quantity;
				} else {
					quantityType = EQuantityTypesLow.QuantityAsMultiplier;
					quantityValue = quantity;
				}
			}
			if (onlyModifiers.length) {
				quantityType = onlyModifiers[0].mappings![0].mapping!.quantityType;
				quantityValue = parseFloat(onlyModifiers[0].mappings![0].mapping!.quantityValue || "1");
			}
			if (onlyModifiers.length || Object.keys(measurementToCellnameMap).includes(measurement)) {
				if (quantityType === EQuantityTypesLow.QuantityAsMultiplier) {
					return `${quantityValue * (quantityBeforeModifier || quantity)} ${getMeasurement(measurement)}`;
				} else if (quantityType === EQuantityTypesLow.QauntityAsSubtract) {
					return `${(quantityBeforeModifier || quantity) + quantityValue} ${getMeasurement(measurement)}`; // quantityValue is negative
				}
			} else {
				const q = quantityBeforeModifierFunc(pageId, measurementToCellnameMap[measurement]);
				return q ? `${q} ${getMeasurement(measurement)}` : `${quantity} ${measurement}`;
			}
		}
		return "na1";
	}

	static fuzzyMatch(str: string) {
		const map = {
			0: ["0", "o", "O"],
			1: ["1", "l", "i", "I", "j", "|"],
			2: ["2", "z", "Z"],
			4: ["4", "A"],
			5: ["5", "S", "s"],
			6: ["6", "b"],
			7: ["7", "T"],
			8: ["8", "B"],
			9: ["9", "g", "q"],
		};
		const pass0 = str
			.split("")
			.map((c) => {
				const found = Object.entries(map).find(([key, value]) => value.includes(c));
				return found ? found[0] : c;
			})
			.join("");
		// now change consecutive duplicates to single character
		const pass1 = pass0.replace(/(.)\1+/g, "$1");
		return pass1;
	}
}
